import {
    Stack,
    Text,
    Loader,
    Title,
    Group as MGroup,
    ActionIcon,
    Divider,
} from "@mantine/core";
import slugify from "@sindresorhus/slugify";
import {TOPIC_TYPE_PATHS} from "../../types";
import IconDotsVertical from "../../assets/dots-vertical.svg?react";
import classes from "./MyWorkPage.module.css";
import {useTranslation} from "react-i18next";
import {useGetApiCollectionsQuery} from "../../dibAdminApiCodegen/dibadminApi";
import {isToday, subDays} from "date-fns";
import {useMemo} from "react";
import {CollectionHistoryItemDto} from "../../dibAdminApiCodegen/dibadminApi";
import {ExperimentalLink} from "../../components/ExperimentalLink";

type Group = {
    label: string;
    items: CollectionHistoryItemDto[];
    showLabel: boolean;
};

function isTopicTypePathKey(key: number): key is keyof typeof TOPIC_TYPE_PATHS {
    return key in TOPIC_TYPE_PATHS;
}

function getUrlForItem(item: CollectionHistoryItemDto) {
    const key = parseInt(item.type ?? "", 10);
    if (isTopicTypePathKey(key)) {
        const topicTypePath = TOPIC_TYPE_PATHS[key];
        const topicPart = `/${topicTypePath}/${slugify(item.name ?? "t")}/${encodeURIComponent(item.topic_id?.toLowerCase() ?? "")}`;
        const collectionPart = `/utfylling/${slugify(item.title ?? "c")}/${encodeURIComponent(item.collection_id.toLowerCase() ?? "")}`;

        return `${topicPart}${collectionPart}`;
    } else {
        return "";
    }
}

export function MyWorkPage() {
    const {data, isLoading} = useGetApiCollectionsQuery({});
    const {t} = useTranslation();

    const groups = useMemo(() => {
        const items = [...(data?.items ?? [])];
        if (items.length === 0) return [];

        items.sort((a, b) => {
            const da = a.lastuse ? new Date(a.lastuse).getTime() : 0;
            const db = b.lastuse ? new Date(b.lastuse).getTime() : 0;
            return db - da;
        });

        const sevenDaysAgo = subDays(new Date(), 7);
        const today: CollectionHistoryItemDto[] = [];
        const lastWeek: CollectionHistoryItemDto[] = [];
        const previous: CollectionHistoryItemDto[] = [];

        for (const item of items) {
            const date = item.lastuse ? new Date(item.lastuse) : null;
            if (date && isToday(date)) {
                today.push(item);
            } else if (date && date >= sevenDaysAgo) {
                lastWeek.push(item);
            } else {
                previous.push(item);
            }
        }

        const hasRecentGroup = today.length > 0 || lastWeek.length > 0;
        const result: Group[] = [];
        if (today.length > 0)
            result.push({
                label: t("date.today"),
                items: today,
                showLabel: true,
            });
        if (lastWeek.length > 0)
            result.push({
                label: t("date.lastWeek"),
                items: lastWeek,
                showLabel: true,
            });
        if (previous.length > 0)
            result.push({
                label: t("date.previously"),
                items: previous,
                showLabel: hasRecentGroup,
            });
        return result;
    }, [data?.items, t]);

    if (isLoading) {
        return (
            <Stack align="center">
                <Loader />
            </Stack>
        );
    }

    if (groups.length === 0) {
        return (
            <Stack>
                <Text c="dimmed">No collections found.</Text>
            </Stack>
        );
    }

    return (
        <Stack gap={0}>
            <Title order={3} mb="md" size={"lg"} className={classes.title}>
                {t("Widget.internal.portal.myWork")}
            </Title>
            {groups.map((group) => (
                <Stack key={group.label} gap={0} className={classes.groupStack}>
                    {group.showLabel && (
                        <Text className={classes.groupLabel}>
                            {group.label}
                        </Text>
                    )}
                    {group.items.map((item, i) => (
                        <MGroup
                            justify="space-between"
                            wrap="nowrap"
                            className={classes.itemRow}
                            key={`myWork-${item.collectionId ?? i}`}
                        >
                            <ExperimentalLink
                                href={getUrlForItem(item)}
                                className={classes.itemLink}
                            >
                                <Text className={classes.itemName}>
                                    {item.title}
                                </Text>
                                {item.name && (
                                    <Text className={classes.itemDescription}>
                                        {item.name}
                                    </Text>
                                )}
                            </ExperimentalLink>
                            <ActionIcon variant="subtle" color="gray" size="sm">
                                <IconDotsVertical width={16} height={16} />
                            </ActionIcon>
                        </MGroup>
                    ))}
                </Stack>
            ))}
        </Stack>
    );
}
